import { useFocusEffect } from "expo-router";
import { useCallback, useEffect, useRef } from "react";

/** Fallback unlock for taps that don't leave the screen (e.g. Linking.openURL). */
const RELEASE_AFTER_MS = 1000;

/**
 * Guards navigation taps so rapid repeat taps don't push the same screen
 * several times (each needing its own back press). The first tap runs and
 * locks; the lock is released when this screen regains focus (user came back)
 * or after RELEASE_AFTER_MS, whichever comes first.
 */
export function useSingleTapAction() {
  const lockedRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const release = useCallback(() => {
    lockedRef.current = false;
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useFocusEffect(release);
  useEffect(() => release, [release]);

  return useCallback(
    (action: () => void) => {
      if (lockedRef.current) {
        return;
      }
      lockedRef.current = true;
      timerRef.current = setTimeout(release, RELEASE_AFTER_MS);
      action();
    },
    [release],
  );
}
